import React, { forwardRef, useImperativeHandle, useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Modal, FormLayout, TextField, BlockStack, Text, InlineGrid, InlineStack, Select, ChoiceList, Button, Tag, Icon, ResourceList, ResourceItem, Divider, Thumbnail, Checkbox, InlineError, Banner, Tooltip } from '@shopify/polaris';
import { ChevronRightIcon, SearchIcon, XIcon, ImageIcon, GlobeIcon, DeleteIcon, PlusIcon } from '@shopify/polaris-icons';
import { ReactCountryFlag } from "react-country-flag"
import { CurrencyFieldCustom } from './CurrencyFieldCustom';
import { PercentageFieldCustom } from './PercentageFieldCustom';
import { QuantityFieldCustom } from './QuantityFieldCustom';
import { ResourcePicker } from './ResourcePicker';
import { ResourcePickerInline } from './ResourcePickerInline';
import { ShippingMethodPickerCustom } from './ShippingMethodPickerCustom';
import { currencyToFloat } from './../Utils';

export const CampaignDiscount = forwardRef((props, ref) => {
    const [model, setModel] = useState(null);
    const [active, setActive] = useState(false);

    const [discountSubTypes] = useState(
        [
            { value: 5, name: "Volume discount", description: "Discount based on a purchase quantity or value." },
            { value: 6, name: "Mix & match", description: "Each product in the set can have its own discount." },
            { value: 1, name: "Amount off products", description: "Discount specific products or collections of products." },
            { value: 2, name: "Buy X get Y", description: "Discount products based on a customer's purchase." },
            { value: 3, name: "Amount off order", description: "Discount the total order amount." },
            { value: 4, name: "Shipping discount", description: "Offer shipping discount on an order." },
        ]
    );

    const [error, setError] = useState({ title: false, valuePercentage: false, valueFixedAmount: false, valueFixedPrice: false, minimumAmount: false, minimumQuantity: false, maximumAmount: false, maximumQuantity: false, skipQuantity: false, getType: false, buyType: false, excludeType: false, onQuantity: false, usesPerOrderLimit: false, maximumShippingPrice: false, countryType: false, shippingMethodType: false });
    const [errorTier, setErrorTier] = useState([]);
    const [errorMix, setErrorMix] = useState([]);
    const [countryPickerOptions, setCountryPickerOptions] = useState({ initialQuery: "", open: false });
    const [selectedCountries, setSelectedCountries] = useState([]);

    const appliesToText = useCallback(() => {
        let helpText = "";

        if (model === null) {
            return helpText;
        }

        if (model.discountSubType === 3) {
            helpText = "Applies to all products.";
        }

        if (model.discountSubType === 6) {
            helpText = "Applies to selected products across all sets.";
        }

        if (model.discountSubType === 1) {
            if (model.getType === 1) {
                helpText = "Applies only to selected collections.";
            }
            else {
                helpText = "Applies only to selected products.";
            }
        }

        if (model.discountSubType === 3 || model.discountSubType === 4) {
            if (model.buyType === 1) {
                helpText = "Applies only to selected collections.";
            }
            else if (model.buyType === 2) {
                helpText = "Applies only to selected products.";
            }
            else {
                helpText = "Applies to all products.";
            }
        }

        return helpText;
    }, [model]);

    const titlePlaceholder = useCallback(() => {
        let text = "";

        if (model === null) {
            return text;
        }

        if (model.discountSubType === 1) {
            if (model.valueType === 1) {
                text = "e.g. 20% off on specific " + (model.getType === 1 ? "collections" : "products");
            }
            else if (model.valueType === 2) {
                text = "e.g. " + props.currencySymbol + " 20.00 off on specific " + (model.getType === 1 ? "collections" : "products");
            }
            else {
                text = "e.g. Sell specific " + (model.getType === 1 ? "collections" : "products") + " at " + props.currencySymbol + " 20.00";
            }
        }

        if (model.discountSubType === 2) {
            text = "e.g. Buy 2 items get one item free";
        }

        if (model.discountSubType === 3) {
            if (model.valueType === 1) {
                text = "e.g. 20% off on entire order";
            }
            else if (model.valueType === 2) {
                text = "e.g. " + props.currencySymbol + " 20.00 off on entire order";
            }
            else {
                text = "e.g. Sell all products at " + props.currencySymbol + " 20.00";
            }
        }

        if (model.discountSubType === 4) {
            text = "e.g. Free shipping on orders over " + props.currencySymbol + " 50.00";
        }

        if (model.discountSubType === 5) {
            text = "e.g. Buy more get more discount";
        }

        if (model.discountSubType === 6) {
            text = "e.g. Mix & match, get discounts";
        }

        return text;
    }, [model, props.currencySymbol]);

    const tierTitlePlaceholder = function (index, valueType) {
        return `e.g. Buy ${(index + 1) * 2} get ${(valueType === 2 ? props.currencySymbol + ' ' : '')}${(index + 1) * 10}${(valueType === 1 ? '%' : '.00')} off`;
    };

    const allowAutoAdd = useCallback(() => {
        let allow = false;

        if (model === null) {
            return allow;
        }

        allow = props.automatic
            && model.discountedValueType === 3
            && model.getType === 2
            && model.getProductResources !== null && model.getProductResources.length === 1 && (model.getProductResources[0].variantIds === null || model.getProductResources[0].variantIds.length === 1);

        return allow;
    }, [props.automatic, model]);

    useImperativeHandle(ref, () => ({
        open(newModel) {
            setError({ title: false, valuePercentage: false, valueFixedAmount: false, valueFixedPrice: false, minimumAmount: false, minimumQuantity: false, maximumAmount: false, maximumQuantity: false, skipQuantity: false, getType: false, buyType: false, excludeType: false, onQuantity: false, usesPerOrderLimit: false, maximumShippingPrice: false, countryType: false, shippingMethodType: false });
            setErrorTier([]);
            setErrorMix([]);
            setModel(newModel);
            setActive(true);
        }
    }));

    const handleError = useCallback(
        (fieldName, hasError) => {
            setError((prevState) => {
                let error = Object.assign({}, prevState);
                error[fieldName] = hasError;
                return error;
            });
        },
        [],
    );

    const handleChange = useCallback(
        (fieldName, newValue) => {
            if (fieldName === 'valueType' || fieldName === 'getType' || fieldName === 'buyType' || fieldName === 'excludeType' || fieldName === 'discountedValueType' || fieldName === 'countryType' || fieldName === 'purchaseType' || fieldName === 'shippingMethodType' || fieldName === 'itemRequirements') {
                newValue = parseInt(newValue);
            }

            if (fieldName === 'getType') {
                handleError('getType', false);
            }

            if (fieldName === 'buyType') {
                handleError('buyType', false);
            }

            if (fieldName === 'excludeType') {
                handleError('excludeType', false);
            }

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model[fieldName] = newValue;
                if (fieldName === 'discountedValueType') {
                    model['valueType'] = newValue === 2 ? 2 : (newValue === 4 ? 3 : 1);
                    if (newValue === 3) {
                        model['valuePercentage'] = '100';
                    }
                }
                return model;
            });
        },
        [handleError],
    );

    const handleChangeTier = useCallback(
        (fieldName, newValue) => {
            let fSplit = fieldName.split('_');
            let index = parseInt(fSplit[0]);
            fieldName = fSplit[1];

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.tiers[index][fieldName] = newValue;
                return model;
            });
        },
        [],
    );

    const handleErrorTier = useCallback(
        (fieldName, hasError) => {
            let l_errorTier = [...errorTier];
            if (l_errorTier.indexOf(fieldName) > -1 && !hasError) {
                l_errorTier.splice(l_errorTier.indexOf(fieldName), 1);
            }
            else if (l_errorTier.indexOf(fieldName) <= -1 && hasError) {
                l_errorTier.push(fieldName);
            }
            setErrorTier(l_errorTier);
        },
        [errorTier],
    );

    const handleAddTier = useCallback(
        () => {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.tiers.push({ title: '', minimumAmount: null, minimumQuantity: null, valueFixedAmount: null, valuePercentage: null });
                return model;
            });
        },
        [],
    );

    const handleRemoveTier = useCallback(
        (index) => {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.tiers.splice(index, 1);
                return model;
            });
        },
        [],
    );

    const handleChangeMix = useCallback(
        (fieldName, newValue) => {
            let fSplit = fieldName.split('_');
            let index = parseInt(fSplit[0]);
            fieldName = fSplit[1];

            if (fieldName === 'valueType' || fieldName === 'purchaseType' || fieldName === 'getType') {
                newValue = parseInt(newValue);
            }

            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.mixes[index][fieldName] = newValue;
                return model;
            });
        },
        [],
    );

    const handleErrorMix = useCallback(
        (fieldName, hasError) => {
            let l_errorMix = [...errorMix];
            if (l_errorMix.indexOf(fieldName) > -1 && !hasError) {
                l_errorMix.splice(l_errorMix.indexOf(fieldName), 1);
            }
            else if (l_errorMix.indexOf(fieldName) <= -1 && hasError) {
                l_errorMix.push(fieldName);
            }
            setErrorMix(l_errorMix);
        },
        [errorMix],
    );

    const handleAddMix = useCallback(
        () => {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.mixes.push({ getType: 2, purchaseType: 0, valueType: 1, valueFixedAmount: null, valueFixedPrice: null, valuePercentage: null });
                return model;
            });
        },
        [],
    );

    const handleRemoveMix = useCallback(
        (index) => {
            setModel((prevState) => {
                let model = Object.assign({}, prevState);
                model.mixes.splice(index, 1);
                return model;
            });
        },
        [],
    );

    const handleResourcePickerSelection = useCallback(
        (res, method, getType, buyType, excludeType, singleMethod) => {
            if (res) {
                if (method === 'get') {
                    if (getType === 1) {
                        handleChange('getCollectionResources', res.map(p => ({ id: p.id.replace("gid://shopify/Collection/", ""), title: p.title, imageUrl: p.image?.originalSrc })));
                        handleChange('getProductResources', null);
                    }
                    if (getType === 2) {
                        handleChange('getProductResources', res.map(p => ({ id: p.id.replace("gid://shopify/Product/", ""), title: p.title, imageUrl: p.images?.[0]?.originalSrc, variantIds: p.variants.map(x => x.id.replace("gid://shopify/ProductVariant/", "")) })));
                        handleChange('getCollectionResources', null);
                    }
                    if (res.length) {
                        handleError('getType', false);
                    }
                    if (singleMethod) {
                        handleChange('buyType', 0);
                        handleChange('buyProductResources', null);
                        handleChange('buyCollectionResources', null);
                        handleError('buyType', false);
                    }
                }
                else if (method === 'buy') {
                    if (buyType === 1) {
                        handleChange('buyCollectionResources', res.map(p => ({ id: p.id.replace("gid://shopify/Collection/", ""), title: p.title, imageUrl: p.image?.originalSrc })));
                        handleChange('buyProductResources', null);
                    }
                    if (buyType === 2) {
                        handleChange('buyProductResources', res.map(p => ({ id: p.id.replace("gid://shopify/Product/", ""), title: p.title, imageUrl: p.images?.[0]?.originalSrc, variantIds: p.variants.map(x => x.id.replace("gid://shopify/ProductVariant/", "")) })));
                        handleChange('buyCollectionResources', null);
                    }
                    if (res.length) {
                        handleError('buyType', false);
                    }
                    if (singleMethod) {
                        handleChange('getType', 0);
                        handleChange('getProductResources', null);
                        handleChange('getCollectionResources', null);
                        handleError('getType', false);
                    }
                }
                else if (method === 'exclude') {
                    if (excludeType === 1) {
                        handleChange('excludeCollectionResources', res.map(p => ({ id: p.id.replace("gid://shopify/Collection/", ""), title: p.title, imageUrl: p.image?.originalSrc })));
                        handleChange('excludeProductResources', null);
                    }
                    if (excludeType === 2) {
                        handleChange('excludeProductResources', res.map(p => ({ id: p.id.replace("gid://shopify/Product/", ""), title: p.title, imageUrl: p.images?.[0]?.originalSrc, variantIds: p.variants.map(x => x.id.replace("gid://shopify/ProductVariant/", "")) })));
                        handleChange('excludeCollectionResources', null);
                    }
                    if (res.length) {
                        handleError('excludeType', false);
                    }
                }
            }
        },
        [handleChange, handleError]
    );

    const handleResourcePickerSelectionInline = useCallback(
        (res, method, getType, buyType, excludeType, index) => {
            if (res) {

                let l_errorMix = [...errorMix];

                if (method === 'get') {
                    if (getType === 1) {
                        handleChangeMix(`${index}_getCollectionResources`, res.map(p => ({ id: p.id.replace("gid://shopify/Collection/", ""), title: p.title, imageUrl: p.image?.originalSrc })));
                        handleChangeMix(`${index}_getProductResources`, null);
                    }
                    if (getType === 2) {
                        handleChangeMix(`${index}_getProductResources`, res.map(p => ({ id: p.id.replace("gid://shopify/Product/", ""), title: p.title, imageUrl: p.images?.[0]?.originalSrc, variantIds: p.variants.map(x => x.id.replace("gid://shopify/ProductVariant/", "")) })));
                        handleChangeMix(`${index}_getCollectionResources`, null);
                    }
                    if (res.length) {
                        let fieldName = `${index}_getType`;
                        if (l_errorMix.indexOf(fieldName) > -1) {
                            l_errorMix.splice(l_errorMix.indexOf(fieldName), 1);
                        }
                    }
                }
                else if (method === 'buy') {
                    if (buyType === 1) {
                        handleChangeMix(`${index}_buyCollectionResources`, res.map(p => ({ id: p.id.replace("gid://shopify/Collection/", ""), title: p.title, imageUrl: p.image?.originalSrc })));
                        handleChangeMix(`${index}_buyProductResources`, null);
                    }
                    if (buyType === 2) {
                        handleChangeMix(`${index}_buyProductResources`, res.map(p => ({ id: p.id.replace("gid://shopify/Product/", ""), title: p.title, imageUrl: p.images?.[0]?.originalSrc, variantIds: p.variants.map(x => x.id.replace("gid://shopify/ProductVariant/", "")) })));
                        handleChangeMix(`${index}_buyCollectionResources`, null);
                    }
                    if (res.length) {
                        let fieldName = `${index}_buyType`;
                        if (l_errorMix.indexOf(fieldName) > -1) {
                            l_errorMix.splice(l_errorMix.indexOf(fieldName), 1);
                        }
                    }
                }
                else if (method === 'exclude') {
                    if (excludeType === 1) {
                        handleChangeMix(`${index}_excludeCollectionResources`, res.map(p => ({ id: p.id.replace("gid://shopify/Collection/", ""), title: p.title, imageUrl: p.image?.originalSrc })));
                        handleChangeMix(`${index}_excludeProductResources`, null);
                    }
                    if (excludeType === 2) {
                        handleChangeMix(`${index}_excludeProductResources`, res.map(p => ({ id: p.id.replace("gid://shopify/Product/", ""), title: p.title, imageUrl: p.images?.[0]?.originalSrc, variantIds: p.variants.map(x => x.id.replace("gid://shopify/ProductVariant/", "")) })));
                        handleChangeMix(`${index}_excludeCollectionResources`, null);
                    }
                    if (res.length) {
                        let fieldName = `${index}_excludeType`;
                        if (l_errorMix.indexOf(fieldName) > -1) {
                            l_errorMix.splice(l_errorMix.indexOf(fieldName), 1);
                        }
                    }
                }

                setErrorMix(l_errorMix);
            }
        },
        [handleChangeMix, errorMix]
    );

    const handleCountryPicker = useCallback(
        (fieldName, newValue) => {
            setCountryPickerOptions((prevState) => {
                let item = Object.assign({}, prevState);
                item[fieldName] = newValue;
                return item;
            });
        },
        [],
    );

    const handleCountryPickerSelection = useCallback(
        () => {
            let includeRestOfWorld = selectedCountries.indexOf('__') > -1;
            let countries = selectedCountries.filter(p => p !== '__').join();

            handleError('countryType', !includeRestOfWorld && !countries);
            handleChange('includeRestOfWorld', includeRestOfWorld);
            handleChange('countries', countries);
            handleCountryPicker('open', false);
        },
        [selectedCountries, handleChange, handleCountryPicker, handleError],
    );

    const countries = function () {

        var codes = model.countries ? model.countries.split(',') : [];
        if (model.includeRestOfWorld) {
            codes.push('__');
        }

        var items = props.user.shippingCountries;

        return items.filter(p => codes.indexOf(p.code) > -1);
    }

    const filteredCountries = useMemo(() => {
        let items = props.user.shippingCountries;
        if (countryPickerOptions.initialQuery) {
            items = items.filter(p =>
                p.name.toLowerCase().includes(countryPickerOptions.initialQuery.toLowerCase())
            );
        }
        return items;
    }, [countryPickerOptions.initialQuery, props.user.shippingCountries]);

    const valueTypeOptions = function (discountSubType) {
        let items = [{ label: 'Percentage', value: 1 }, { label: 'Fixed amount', value: 2 }];
        if (discountSubType === 1 || discountSubType === 2 || discountSubType === 4 || discountSubType === 6) {
            items.push({ label: 'Fixed price', value: 3 });
        }
        return items;
    }

    const valueTypeHelpText = function (discountSubType, valueType) {
        let helpText = '';

        if (discountSubType === 1) {
            if (valueType === 1) {
                helpText = 'Applies a percentage reduction to products.';
            }
            else if (valueType === 2) {
                helpText = 'Subtracts a fixed amount from products.';
            }
            else if (valueType === 3) {
                helpText = 'Sets a fixed price for products.';
            }
        }
        else if (discountSubType === 3) {
            if (valueType === 1) {
                helpText = 'Applies a percentage reduction to order.';
            }
            else if (valueType === 2) {
                helpText = 'Subtracts a fixed amount from order.';
            }
        }
        else if (discountSubType === 4) {
            if (valueType === 1) {
                helpText = 'Applies a percentage reduction to shipping.';
            }
            else if (valueType === 2) {
                helpText = 'Subtracts a fixed amount from shipping.';
            }
            else if (valueType === 3) {
                helpText = 'Sets a fixed price for shipping.';
            }
        }

        return helpText;
    }

    const handleRemoveResource = useCallback(
        (method, index) => {
            if (method === 'get' && model.getType === 1) {
                let l_resources = model.getCollectionResources;
                let item = l_resources[index];
                l_resources.splice(l_resources.indexOf(item), 1);
                handleChange('getCollectionResources', l_resources);
            }

            if (method === 'get' && model.getType === 2) {
                let l_resources = model.getProductResources;
                let item = l_resources[index];
                l_resources.splice(l_resources.indexOf(item), 1);
                handleChange('getProductResources', l_resources);
            }

            if (method === 'buy' && model.buyType === 1) {
                let l_resources = model.buyCollectionResources;
                let item = l_resources[index];
                l_resources.splice(l_resources.indexOf(item), 1);
                handleChange('buyCollectionResources', l_resources);
            }

            if (method === 'buy' && model.buyType === 2) {
                let l_resources = model.buyProductResources;
                let item = l_resources[index];
                l_resources.splice(l_resources.indexOf(item), 1);
                handleChange('buyProductResources', l_resources);
            }

            if (method === 'exclude' && model.excludeType === 1) {
                let l_resources = model.excludeCollectionResources;
                let item = l_resources[index];
                l_resources.splice(l_resources.indexOf(item), 1);
                handleChange('excludeCollectionResources', l_resources);
            }

            if (method === 'exclude' && model.excludeType === 2) {
                let l_resources = model.excludeProductResources;
                let item = l_resources[index];
                l_resources.splice(l_resources.indexOf(item), 1);
                handleChange('excludeProductResources', l_resources);
            }
        },
        // eslint-disable-next-line
        [model],
    );

    const handleRemoveCountry = useCallback(
        (code) => {
            if (code === '__') {
                handleChange('includeRestOfWorld', false);
            }
            else {
                let cCodes = model.countries.split(',');
                cCodes.splice(cCodes.indexOf(code), 1);
                handleChange('countries', cCodes.join());
            }
        },
        // eslint-disable-next-line
        [model],
    );

    const toggleModal = useCallback(
        () => {
            if (countryPickerOptions.open) {
                handleCountryPicker('open', false);
            }
            else {
                if (active) {
                    setModel(null);
                }
                setActive(!active);
            }
        },
        [active, countryPickerOptions.open, handleCountryPicker],
    );

    const saveChanges = async () => {
        if (model.autoAdd && !allowAutoAdd()) {
            model.autoAdd = false;
        }

        if (model.discountSubType === 3 || model.discountSubType === 4) {
            if (model.itemRequirements === 0) {
                model.getType = 0;
                model.buyType = 0;
            }
            else if (model.itemRequirements === 1) {
                model.getType = 0;
            }
            else if (model.itemRequirements === 2) {
                model.buyType = 0;
            }
        }

        if (model.discountSubType === 1 && model.valueType === 2 && !model.appliesOnEachItem) {
            model.maxLimits.length = 0;
        }

        var hasError = false;
        if (!model.title) {
            handleError('title', true);
            hasError = true;
        }

        if (model.discountSubType !== 5 && model.discountSubType !== 6 && model.valueType === 1 && (!model.valuePercentage || parseInt(model.valuePercentage) === 0)) {
            handleError('valuePercentage', true);
            hasError = true;
        }

        if (model.discountSubType !== 5 && model.discountSubType !== 6 && model.valueType === 2 && (!model.valueFixedAmount || currencyToFloat(model.valueFixedAmount) === 0)) {
            handleError('valueFixedAmount', true);
            hasError = true;
        }

        if (model.discountSubType !== 5 && model.discountSubType !== 6 && model.valueType === 3 && (!model.valueFixedPrice || currencyToFloat(model.valueFixedPrice) === 0)) {
            handleError('valueFixedPrice', true);
            hasError = true;
        }

        if (model.discountSubType !== 5 && model.minRequirements.includes(1) && (!model.minimumAmount || currencyToFloat(model.minimumAmount) === 0)) {
            handleError('minimumAmount', true);
            hasError = true;
        }

        if (model.discountSubType !== 5 && model.minRequirements.includes(2) && (!model.minimumQuantity || parseInt(model.minimumQuantity) === 0)) {
            handleError('minimumQuantity', true);
            hasError = true;
        }

        if (model.maxLimits.includes(1) && (!model.maximumAmount || currencyToFloat(model.maximumAmount) === 0)) {
            handleError('maximumAmount', true);
            hasError = true;
        }

        if (model.maxLimits.includes(2) && (!model.maximumQuantity || parseInt(model.maximumQuantity) === 0)) {
            handleError('maximumQuantity', true);
            hasError = true;
        }

        if (model.maxLimits.includes(3) && (!model.skipQuantity || parseInt(model.skipQuantity) === 0)) {
            handleError('skipQuantity', true);
            hasError = true;
        }

        if ((model.discountSubType === 1 || model.discountSubType === 2 || model.discountSubType === 4 || model.discountSubType === 5) && ((model.getType === 1 && !model.getCollectionResources?.length) || (model.getType === 2 && !model.getProductResources?.length))) {
            handleError('getType', true);
            hasError = true;
        }

        if ((model.discountSubType === 2 || model.discountSubType === 3 || model.discountSubType === 4) && ((model.buyType === 1 && !model.buyCollectionResources?.length) || (model.buyType === 2 && !model.buyProductResources?.length))) {
            handleError('buyType', true);
            hasError = true;
        }

        if ((model.discountSubType === 1 || model.discountSubType === 3 || model.discountSubType === 5) && ((model.excludeType === 1 && !model.excludeCollectionResources?.length) || (model.excludeType === 2 && !model.excludeProductResources?.length))) {
            handleError('excludeType', true);
            hasError = true;
        }

        if (model.discountSubType === 2 && (!model.onQuantity || parseInt(model.onQuantity) === 0)) {
            handleError('onQuantity', true);
            hasError = true;
        }

        if (model.discountSubType === 2 && model.setUsesPerOrderLimit && (!model.usesPerOrderLimit || parseInt(model.usesPerOrderLimit) === 0)) {
            handleError('usesPerOrderLimit', true);
            hasError = true;
        }

        if (model.discountSubType === 4 && model.setMaximumShippingPrice && (!model.maximumShippingPrice || currencyToFloat(model.maximumShippingPrice) === 0)) {
            handleError('maximumShippingPrice', true);
            hasError = true;
        }

        if (model.countryType === 1 && !model.countries && !model.includeRestOfWorld) {
            handleError('countryType', true);
            hasError = true;
        }

        if (model.shippingMethodType === 1 && !model.shippingMethods) {
            handleError('shippingMethodType', true);
            hasError = true;
        }

        if (model.discountSubType === 5) {

            let l_errorTier = [...errorTier];

            model.tiers.forEach(function (item, index) {
                let fieldName = '';

                if (!item.title) {
                    fieldName = `${index}_title`;
                    if (l_errorTier.indexOf(fieldName) <= -1) {
                        l_errorTier.push(fieldName);
                    }
                    hasError = true;
                }
                if (model.minRequirements.includes(2) && (!item.minimumQuantity || parseInt(item.minimumQuantity) === 0)) {
                    fieldName = `${index}_minimumQuantity`;
                    if (l_errorTier.indexOf(fieldName) <= -1) {
                        l_errorTier.push(fieldName);
                    }
                    hasError = true;
                }
                if (model.minRequirements.includes(1) && (!item.minimumAmount || currencyToFloat(item.minimumAmount) === 0)) {
                    fieldName = `${index}_minimumAmount`;
                    if (l_errorTier.indexOf(fieldName) <= -1) {
                        l_errorTier.push(fieldName);
                    }
                    hasError = true;
                }
                if (model.valueType === 1 && (!item.valuePercentage || parseInt(item.valuePercentage) === 0)) {
                    fieldName = `${index}_valuePercentage`;
                    if (l_errorTier.indexOf(fieldName) <= -1) {
                        l_errorTier.push(fieldName);
                    }
                    hasError = true;
                }
                if (model.valueType === 2 && (!item.valueFixedAmount || currencyToFloat(item.valueFixedAmount) === 0)) {
                    fieldName = `${index}_valueFixedAmount`;
                    if (l_errorTier.indexOf(fieldName) <= -1) {
                        l_errorTier.push(fieldName);
                    }
                    hasError = true;
                }
            });

            setErrorTier(l_errorTier);
        }

        if (model.discountSubType === 6) {

            let l_errorMix = [...errorMix];

            model.mixes.forEach(function (item, index) {
                let fieldName = '';

                if ((item.getType === 1 && !item.getCollectionResources?.length) || (item.getType === 2 && !item.getProductResources?.length)) {
                    fieldName = `${index}_getType`;
                    if (l_errorMix.indexOf(fieldName) <= -1) {
                        l_errorMix.push(fieldName);
                    }
                    hasError = true;
                }
                if (item.valueType === 1 && (!item.valuePercentage || parseInt(item.valuePercentage) === 0)) {
                    fieldName = `${index}_valuePercentage`;
                    if (l_errorMix.indexOf(fieldName) <= -1) {
                        l_errorMix.push(fieldName);
                    }
                    hasError = true;
                }
                if ((item.valueType === 2 || item.valueType === 4) && (!item.valueFixedAmount || currencyToFloat(item.valueFixedAmount) === 0)) {
                    fieldName = `${index}_valueFixedAmount`;
                    if (l_errorMix.indexOf(fieldName) <= -1) {
                        l_errorMix.push(fieldName);
                    }
                    hasError = true;
                }
                if (item.valueType === 3 && (!item.valueFixedPrice || currencyToFloat(item.valueFixedPrice) === 0)) {
                    fieldName = `${index}_valueFixedPrice`;
                    if (l_errorMix.indexOf(fieldName) <= -1) {
                        l_errorMix.push(fieldName);
                    }
                    hasError = true;
                }
            });

            setErrorMix(l_errorMix);
        }

        if (!hasError) {
            if (model.getType === 0) {
                model.getProductResources = null;
                model.getCollectionResources = null;
            }

            if (model.buyType === 0) {
                model.buyProductResources = null;
                model.buyCollectionResources = null;
            }

            if ((model.discountSubType === 1 || model.discountSubType === 5) && model.getType === 2) {
                model.excludeType = 0;
            }

            if (model.excludeType === 0) {
                model.excludeProductResources = null;
                model.excludeCollectionResources = null;
            }

            props.handleSaveDiscount(model);
            toggleModal();
        }
    };

    const renderMinimumAmount = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <div className="choice-list-input">
                    <CurrencyFieldCustom
                        prefix={props.currencySymbol}
                        helpText={appliesToText()}
                        fieldName="minimumAmount"
                        value={model.minimumAmount}
                        required={true}
                        error={error.minimumAmount ? "Minimum purchase can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, props.currencySymbol, appliesToText, error.minimumAmount],
    );

    const renderMinimumQuantity = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        helpText={appliesToText()}
                        fieldName="minimumQuantity"
                        value={model.minimumQuantity}
                        required={true}
                        error={error.minimumQuantity ? "Minimum quantity can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, appliesToText, error.minimumQuantity],
    );

    const renderMaximumAmount = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <div className="choice-list-input">
                    <CurrencyFieldCustom
                        prefix={props.currencySymbol}
                        helpText={"This discount will not exceed the entered amount."}
                        fieldName="maximumAmount"
                        value={model.maximumAmount}
                        required={true}
                        error={error.maximumAmount ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, props.currencySymbol, error.maximumAmount],
    );

    const renderMaximumQuantity = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        helpText={'Discount will apply to a maximum of N quantities.'}
                        fieldName="maximumQuantity"
                        value={model.maximumQuantity}
                        required={true}
                        error={error.maximumQuantity ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.maximumQuantity],
    );

    const renderSkipQuantity = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <div className="choice-list-input">
                    <QuantityFieldCustom
                        helpText={'Discount starts applying after the first N quantities.'}
                        fieldName="skipQuantity"
                        value={model.skipQuantity}
                        required={true}
                        error={error.skipQuantity ? "This field can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.skipQuantity],
    );

    const renderPercentage = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <div className="choice-list-input">
                    <PercentageFieldCustom
                        fieldName="valuePercentage"
                        value={model.valuePercentage}
                        required={true}
                        error={error.valuePercentage ? "Discount value can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, error.valuePercentage],
    );

    const renderAmountOffEach = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <div className="choice-list-input">
                    <CurrencyFieldCustom
                        prefix={props.currencySymbol}
                        helpText="For multiple quantities, the discount amount will be taken off each Y item."
                        fieldName="valueFixedAmount"
                        value={model.valueFixedAmount}
                        required={true}
                        error={error.valueFixedAmount ? "Discount value can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, props.currencySymbol, error.valueFixedAmount],
    );

    const renderFixedPrice = useCallback(
        (isSelected) =>
            isSelected && model !== null && (
                <div className="choice-list-input">
                    <CurrencyFieldCustom
                        prefix={props.currencySymbol}
                        helpText="Reduces product prices to the specified fixed value."
                        fieldName="valueFixedPrice"
                        value={model.valueFixedPrice}
                        required={true}
                        error={error.valueFixedPrice ? "Price value can't be blank" : ''}
                        handleChange={handleChange}
                        handleError={handleError}
                    />
                </div>
            ),
        [handleChange, handleError, model, props.currencySymbol, error.valueFixedPrice],
    );

    const renderPurchaseTypeHelpText = useCallback(
        () => {
            return (
                model !== null && model.purchaseType !== 1 ?
                    <Text as="span" tone="subdued">{"Discount will apply only to the first payment for subscriptions."}</Text>
                    : null
            )
        },
        [model]
    );

    const renderPurchaseTypeSelect = useCallback(
        (showHelpText) => {
            return (
                model !== null ?
                    <Select
                        label="Purchase type"
                        options={[{ label: 'Both', value: 0 }, { label: 'One-time purchase', value: 1 }, { label: 'Subscription', value: 2 }]}
                        value={model.purchaseType}
                        onChange={(newValue) => handleChange('purchaseType', newValue)}
                        helpText={showHelpText && model.purchaseType !== 1 ? "Discount will apply only to the first payment for subscriptions." : ""}
                    />
                    : null
            )
        },
        [handleChange, model],
    );

    const renderResourcePicker = useCallback(
        (method, errorText, showMethodSelect, singleMethod) => {
            const methodType = method === 'get' ? model?.getType : (method === 'buy' ? model?.buyType : model?.excludeType);
            const errorType = method === 'get' ? error.getType : (method === 'buy' ? error.buyType : error.excludeType);
            let resources;
            let selectionIds;
            if (method === 'get') {
                resources = methodType === 1 ? model?.getCollectionResources : model?.getProductResources;
                selectionIds = methodType === 1 ? resources?.map(p => ({ id: `gid://shopify/Collection/${p.id}` })) : resources?.map(p => ({ id: `gid://shopify/Product/${p.id}`, variants: p.variantIds?.map(x => ({ id: `gid://shopify/ProductVariant/${x}` })) }));
            }
            else if (method === 'buy') {
                resources = methodType === 1 ? model?.buyCollectionResources : model?.buyProductResources;
                selectionIds = methodType === 1 ? resources?.map(p => ({ id: `gid://shopify/Collection/${p.id}` })) : resources?.map(p => ({ id: `gid://shopify/Product/${p.id}`, variants: p.variantIds?.map(x => ({ id: `gid://shopify/ProductVariant/${x}` })) }));
            }
            else if (method === 'exclude') {
                resources = methodType === 1 ? model?.excludeCollectionResources : model?.excludeProductResources;
                selectionIds = methodType === 1 ? resources?.map(p => ({ id: `gid://shopify/Collection/${p.id}` })) : resources?.map(p => ({ id: `gid://shopify/Product/${p.id}`, variants: p.variantIds?.map(x => ({ id: `gid://shopify/ProductVariant/${x}` })) }));
            }
            if (!resources) {
                resources = [];
            }

            return (
                model !== null ?
                    <>
                        <ResourcePicker
                            methodType={methodType}
                            method={method}
                            getType={model.getType}
                            buyType={model.buyType}
                            excludeType={model.excludeType}
                            errorType={errorType}
                            errorText={errorText}
                            selectionIds={selectionIds}
                            handleChange={handleChange}
                            handleResourcePickerSelection={handleResourcePickerSelection}
                            showMethodSelect={showMethodSelect}
                            singleMethod={singleMethod}
                        />
                        {
                            resources.length ?
                                <div className="selected-resources">
                                    <ResourceList
                                        items={resources}
                                        renderItem={(item, id, index) => {
                                            return (
                                                <ResourceItem
                                                    verticalAlignment="center"
                                                    media={<Thumbnail source={item.imageUrl ? item.imageUrl : ImageIcon} size="small" />}
                                                >
                                                    <InlineStack wrap={false}>
                                                        <div className="w-100" data-handle={props.user.adminEnabled && item.handle ? item.handle : ""}>
                                                            <Text variant="bodyMd" as="h3">{item.title}</Text>
                                                            {
                                                                item.subtitle ?
                                                                    <Text as="p" tone="subdued">{item.subtitle}</Text>
                                                                    : null
                                                            }
                                                        </div>
                                                        <div>
                                                            <Button variant="monochromePlain" onClick={() => handleRemoveResource(method, index)}><Icon source={XIcon} /></Button>
                                                        </div>
                                                    </InlineStack>
                                                </ResourceItem>
                                            );
                                        }}
                                    />
                                </div>
                                : null
                        }
                    </>
                    : null
            )
        },
        [model, error.getType, error.buyType, error.excludeType, handleRemoveResource, handleChange, handleResourcePickerSelection],
    );

    const renderResourcePickerInline = useCallback(
        (obj, index, method) => {
            const methodType = method === 'get' ? obj?.getType : (method === 'buy' ? obj?.buyType : obj?.excludeType);
            const errorType = method === 'get' ? errorMix.indexOf(`${index}_getType`) > -1 : (method === 'buy' ? errorMix.indexOf(`${index}_buyType`) > -1 : errorMix.indexOf(`${index}_excludeType`) > -1);
            let resources;
            let selectionIds;
            if (method === 'get') {
                resources = methodType === 1 ? obj?.getCollectionResources : obj?.getProductResources;
                selectionIds = methodType === 1 ? resources?.map(p => ({ id: `gid://shopify/Collection/${p.id}` })) : resources?.map(p => ({ id: `gid://shopify/Product/${p.id}`, variants: p.variantIds?.map(x => ({ id: `gid://shopify/ProductVariant/${x}` })) }));
            }
            else if (method === 'buy') {
                resources = methodType === 1 ? obj?.buyCollectionResources : obj?.buyProductResources;
                selectionIds = methodType === 1 ? resources?.map(p => ({ id: `gid://shopify/Collection/${p.id}` })) : resources?.map(p => ({ id: `gid://shopify/Product/${p.id}`, variants: p.variantIds?.map(x => ({ id: `gid://shopify/ProductVariant/${x}` })) }));
            }
            else if (method === 'exclude') {
                resources = methodType === 1 ? obj?.excludeCollectionResources : obj?.excludeProductResources;
                selectionIds = methodType === 1 ? resources?.map(p => ({ id: `gid://shopify/Collection/${p.id}` })) : resources?.map(p => ({ id: `gid://shopify/Product/${p.id}`, variants: p.variantIds?.map(x => ({ id: `gid://shopify/ProductVariant/${x}` })) }));
            }
            if (!resources) {
                resources = [];
            }

            return (
                obj !== null ?
                    <ResourcePickerInline
                        resources={resources}
                        methodType={methodType}
                        method={method}
                        getType={obj.getType}
                        buyType={obj.buyType}
                        excludeType={obj.excludeType}
                        index={index}
                        errorType={errorType}
                        selectionIds={selectionIds}
                        handleResourcePickerSelection={handleResourcePickerSelectionInline}
                    />
                    : null
            )
        },
        [errorMix, handleResourcePickerSelectionInline],
    );

    const maxLimitsChoices = useCallback(() => {
        const maxLimitsChoices = [];

        if (model === null) {
            return maxLimitsChoices;
        }

        //if (model.discountSubType === 1 || model.discountSubType === 3) {
        //    maxLimitsChoices.push({ label: `Maximum discount amount (${props.currencySymbol})`, value: 1, renderChildren: renderMaximumAmount });
        //}

        maxLimitsChoices.push({ label: 'Maximum quantity to discount', value: 2, renderChildren: renderMaximumQuantity });
        maxLimitsChoices.push({ label: 'Do not discount first N quantities', value: 3, renderChildren: renderSkipQuantity });

        return maxLimitsChoices;
    }, [model, renderMaximumQuantity, renderSkipQuantity]); //renderMaximumAmount, props.currencySymbol

    const itemRequirementsChoices = useCallback(() => {
        const choices = [
            { label: 'Any item', value: 0 },
            { label: 'When specific items are in cart', value: 1 }
        ];

        if (model.discountSubType === 4) {
            choices.push({ label: 'When specific items are not in cart', value: 2 });
        }

        return choices;
    }, [model]);

    return (
        <Modal
            title={countryPickerOptions.open ? (model !== null && (model.countries || model.includeRestOfWorld) ? 'Edit countries' : 'Add countries') : (model === null ? 'Select discount type' : discountSubTypes.filter(p => p.value === model.discountSubType)[0].name)}
            open={active}
            onClose={toggleModal}
            primaryAction={model !== null ? {
                content: countryPickerOptions.open ? (model !== null && (model.countries || model.includeRestOfWorld) ? 'Done' : 'Add') : 'Apply',
                onAction: () => { countryPickerOptions.open ? handleCountryPickerSelection() : saveChanges() }
            } : null}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: toggleModal,
                },
            ]}
        >
            {
                countryPickerOptions.open ?
                    <div className="country-picker">
                        <div className="modal-sticky-bar">
                            <Modal.Section>
                                <TextField
                                    value={countryPickerOptions.initialQuery}
                                    focused={true}
                                    placeholder={'Search countries'}
                                    prefix={<Icon source={SearchIcon} tone="subdued" />}
                                    onChange={(newValue) => handleCountryPicker('initialQuery', newValue)}
                                />
                            </Modal.Section>
                        </div>
                        <ResourceList
                            key={countryPickerOptions.initialQuery}
                            items={filteredCountries}
                            selectedItems={selectedCountries}
                            onSelectionChange={(newValue) => setSelectedCountries(newValue)}
                            renderItem={(item) => {
                                return (
                                    <ResourceItem
                                        verticalAlignment="center"
                                        id={item.code}
                                        name={item.name}
                                        media={
                                            item.code === '__' ?
                                                <div className="rest-of-world-icon"><Icon source={GlobeIcon} /></div>
                                                :
                                                <ReactCountryFlag countryCode={item.code} svg style={{ width: '1.5rem', height: '1.5rem' }} />
                                        }
                                        onClick={() => {
                                            document.querySelector('.country-picker').querySelector(`.Polaris-Box#${item.code}`).querySelector('.Polaris-Checkbox__Input').click();
                                        }}
                                    >
                                        <Text>{item.name}</Text>
                                    </ResourceItem>
                                );
                            }}
                            selectable={true}
                            showHeader={false}
                        />
                    </div>
                    :
                    <div>
                        {
                            model !== null ?
                                <Modal.Section>
                                    <FormLayout>
                                        <TextField
                                            value={model.title}
                                            label="Title"
                                            helpText="Customers will see this in the app's widget."
                                            placeholder={titlePlaceholder()}
                                            error={error.title ? "Title can't be blank" : ''}
                                            onChange={(newValue) => handleChange('title', newValue)}
                                            onBlur={() => handleError('title', !model.title)}
                                        />
                                        {/* #region Discount Value */}
                                        {
                                            model.discountSubType === 1 || model.discountSubType === 3 || model.discountSubType === 4 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 1 || model.discountSubType === 3 || model.discountSubType === 4 ?
                                                <BlockStack gap="100">
                                                    <Text>Discount Value</Text>
                                                    <InlineGrid gap="300" columns={['twoThirds', 'oneThird']}>
                                                        <Select
                                                            options={valueTypeOptions(model.discountSubType)}
                                                            value={model.valueType}
                                                            onChange={(newValue) => handleChange('valueType', newValue)}
                                                            helpText={valueTypeHelpText(model.discountSubType, model.valueType)}
                                                        />
                                                        {
                                                            model.valueType === 1 ?
                                                                <PercentageFieldCustom
                                                                    id="valuePercentage"
                                                                    fieldName="valuePercentage"
                                                                    value={model.valuePercentage}
                                                                    required={true}
                                                                    error={error.valuePercentage ? "Discount value required" : ""}
                                                                    handleChange={handleChange}
                                                                    handleError={handleError}
                                                                />
                                                                :
                                                                model.valueType === 2 ?
                                                                    <CurrencyFieldCustom
                                                                        id="valueFixedAmount"
                                                                        prefix={props.currencySymbol}
                                                                        fieldName="valueFixedAmount"
                                                                        value={model.valueFixedAmount}
                                                                        required={true}
                                                                        error={error.valueFixedAmount ? "Discount value required" : ""}
                                                                        handleChange={handleChange}
                                                                        handleError={handleError}
                                                                    />
                                                                    :
                                                                    <CurrencyFieldCustom
                                                                        id="valueFixedPrice"
                                                                        prefix={props.currencySymbol}
                                                                        fieldName="valueFixedPrice"
                                                                        value={model.valueFixedPrice}
                                                                        required={true}
                                                                        error={error.valueFixedPrice ? "Price value required" : ""}
                                                                        handleChange={handleChange}
                                                                        handleError={handleError}
                                                                    />

                                                        }
                                                    </InlineGrid>
                                                    {
                                                        model.discountSubType === 4 ?
                                                            <Banner>
                                                                <p>
                                                                    {model.valueType === 1 && parseInt(model.valuePercentage) === 100 ? "Percentage set to 100% currently provides free shipping." : "Set Percentage to 100% to offer free shipping."}
                                                                </p>
                                                            </Banner>
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {
                                            model.discountSubType === 3 || model.discountSubType === 4 ?
                                                renderPurchaseTypeSelect(true)
                                                : null
                                        }
                                        {
                                            model.discountSubType === 1 ?
                                                <BlockStack gap="200">
                                                    <InlineGrid gap="300" columns={['twoThirds', 'oneThird']}>
                                                        <Select
                                                            label="Applies to"
                                                            options={[{ label: model.purchaseType === 2 ? 'Specific subscription products' : 'Specific products', value: 2 }, { label: model.purchaseType === 2 ? 'All subscription products in a specific collection' : 'Specific collections', value: 1 }]}
                                                            value={model.getType}
                                                            onChange={(newValue) => handleChange('getType', newValue)}
                                                        />
                                                        {renderPurchaseTypeSelect(false)}
                                                    </InlineGrid>
                                                    {renderResourcePicker('get', `Specific ${model.getType === 1 ? 'collections' : 'products'} must be added`, false, false)}
                                                    {renderPurchaseTypeHelpText()}
                                                    {
                                                        model.valueType === 2 ?
                                                            <Checkbox
                                                                label="Only apply discount once per order"
                                                                helpText="If not selected, the amount will be taken off each eligible item in an order."
                                                                checked={!model.appliesOnEachItem}
                                                                onChange={(newValue) => handleChange('appliesOnEachItem', !newValue)}
                                                            />
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Discount Value */}
                                        {/* #region BXGY */}
                                        {
                                            model.discountSubType === 2 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 2 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="semibold">{model.minRequirements.includes(2) ? "Customer buys" : "Customer spends"}</Text>
                                                    <ChoiceList
                                                        choices={[
                                                            { label: 'Minimum quantity of items', value: 2 },
                                                            { label: 'Minimum purchase amount', value: 1 },
                                                        ]}
                                                        selected={model.minRequirements}
                                                        onChange={(newValue) => handleChange('minRequirements', newValue)}
                                                    />
                                                    <InlineGrid gap="300" columns={['oneThird', 'twoThirds']}>
                                                        {
                                                            model.minRequirements.includes(2) ?
                                                                <QuantityFieldCustom
                                                                    id="minimumQuantity"
                                                                    label="Quantity"
                                                                    fieldName="minimumQuantity"
                                                                    value={model.minimumQuantity}
                                                                    required={true}
                                                                    error={error.minimumQuantity}
                                                                    handleChange={handleChange}
                                                                    handleError={handleError}
                                                                />
                                                                :
                                                                <CurrencyFieldCustom
                                                                    id="minimumAmount"
                                                                    prefix={props.currencySymbol}
                                                                    label="Amount"
                                                                    fieldName="minimumAmount"
                                                                    value={model.minimumAmount}
                                                                    required={true}
                                                                    error={error.minimumAmount}
                                                                    handleChange={handleChange}
                                                                    handleError={handleError}
                                                                />
                                                        }
                                                        <Select
                                                            label="Any items from"
                                                            options={[{ label: 'Specific products', value: 2 }, { label: 'Specific collections', value: 1 }, { label: 'Entire store', value: 0 }]}
                                                            value={model.buyType}
                                                            onChange={(newValue) => handleChange('buyType', newValue)}
                                                        />
                                                    </InlineGrid>
                                                    {
                                                        model.minRequirements.includes(2) && error.minimumQuantity ?
                                                            <InlineError message="Specify a quantity of products the customer must buy" fieldID="minimumQuantity" />
                                                            : null
                                                    }
                                                    {
                                                        model.minRequirements.includes(1) && error.minimumAmount ?
                                                            <InlineError message="Specify an amount the customer must spend" fieldID="minimumAmount" />
                                                            : null
                                                    }
                                                    {
                                                        model.buyType !== 0 ?
                                                            renderResourcePicker('buy', `Specify ${model.buyType === 1 ? 'collections' : 'products'} the customer must buy`, false, false)
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {
                                            model.discountSubType === 2 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 2 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="semibold">Customer gets</Text>
                                                    <Text as="p" tone="subdued">Customers must add the quantity of items specified below to their cart.</Text>
                                                    <InlineGrid gap="300" columns={['oneThird', 'twoThirds']}>
                                                        <QuantityFieldCustom
                                                            id="onQuantity"
                                                            label="Quantity"
                                                            fieldName="onQuantity"
                                                            value={model.onQuantity}
                                                            required={true}
                                                            error={error.onQuantity}
                                                            handleChange={handleChange}
                                                            handleError={handleError}
                                                        />
                                                        <Select
                                                            label="Any items from"
                                                            options={[{ label: 'Specific products', value: 2 }, { label: 'Specific collections', value: 1 }]}
                                                            value={model.getType}
                                                            onChange={(newValue) => handleChange('getType', newValue)}
                                                        />
                                                    </InlineGrid>
                                                    {
                                                        error.onQuantity ?
                                                            <InlineError message="Specify a quantity of products the customer will get" fieldID="onQuantity" />
                                                            : null
                                                    }
                                                    {renderResourcePicker('get', `Specify ${model.getType === 1 ? 'collections' : 'products'} the customer will get`, false, false)}
                                                    <ChoiceList
                                                        title="At a discounted value or fixed price"
                                                        choices={[
                                                            { label: 'Percentage', value: 1, renderChildren: renderPercentage },
                                                            { label: 'Amount off each', value: 2, renderChildren: renderAmountOffEach },
                                                            { label: 'Free', value: 3 },
                                                            { label: 'Fixed price', value: 4, renderChildren: renderFixedPrice },
                                                        ]}
                                                        selected={[model.discountedValueType]}
                                                        onChange={(newValue) => handleChange('discountedValueType', newValue[0])}
                                                    />
                                                </BlockStack>
                                                : null
                                        }
                                        {
                                            model.discountSubType === 2 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 2 ?
                                                <BlockStack gap="100">
                                                    <BlockStack gap="50">
                                                        <Checkbox
                                                            label="Set a maximum number of uses per order"
                                                            checked={model.setUsesPerOrderLimit}
                                                            onChange={(newValue) => handleChange('setUsesPerOrderLimit', newValue)}
                                                        />
                                                        {
                                                            model.setUsesPerOrderLimit ?
                                                                <div className="Polaris-ChoiceList__ChoiceChildren choice-list-input">
                                                                    <QuantityFieldCustom
                                                                        fieldName="usesPerOrderLimit"
                                                                        value={model.usesPerOrderLimit}
                                                                        required={true}
                                                                        error={error.usesPerOrderLimit ? "Maximum number of uses per order can't be blank" : ''}
                                                                        handleChange={handleChange}
                                                                        handleError={handleError}
                                                                    />
                                                                </div>
                                                                : null
                                                        }
                                                    </BlockStack>
                                                    {
                                                        props.user.myShopifyDomain === "discounts-02.myshopify.com"
                                                            || props.user.myShopifyDomain === "discounts-07.myshopify.com"
                                                            || props.user.myShopifyDomain === "c1ecbc-b5.myshopify.com"
                                                            || props.user.myShopifyDomain === "ef0cee-2e.myshopify.com"
                                                            || props.user.myShopifyDomain === "fd9f9f.myshopify.com"
                                                            || props.user.myShopifyDomain === "d0axxa-bv.myshopify.com"
                                                            || props.user.myShopifyDomain === "745e73-ea.myshopify.com"
                                                            || props.user.myShopifyDomain === "sullenclothing.myshopify.com"
                                                            || props.user.myShopifyDomain === "customiama.myshopify.com"
                                                            || props.user.myShopifyDomain === "shop-1hotels-com.myshopify.com"
                                                            || props.user.myShopifyDomain === "aquaction.myshopify.com" ?
                                                            <Checkbox
                                                                disabled={!allowAutoAdd()}
                                                                label="Auto-add items"
                                                                helpText="Limited: only for automatic, free, single variant."
                                                                checked={allowAutoAdd() && model.autoAdd}
                                                                onChange={(newValue) => handleChange('autoAdd', newValue)}
                                                            />
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion BXGY */}
                                        {/* #region VOLUME */}
                                        {
                                            model.discountSubType === 5 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 5 ?
                                                <BlockStack gap="200">
                                                    <InlineGrid gap="300" columns={2}>
                                                        <div>
                                                            <Select
                                                                label="Condition type"
                                                                options={[{ label: 'Minimum quantity', value: 2 }, { label: 'Minimum amount', value: 1 }]}
                                                                value={model.minRequirements[0]}
                                                                onChange={(newValue) => handleChange('minRequirements', [parseInt(newValue)])}
                                                            />
                                                        </div>
                                                        <div>
                                                            <Select
                                                                label="Value type"
                                                                options={[{ label: 'Percentage', value: 1 }, { label: 'Fixed amount', value: 2 }]}
                                                                value={model.valueType}
                                                                onChange={(newValue) => handleChange('valueType', newValue)}
                                                            />
                                                        </div>
                                                    </InlineGrid>
                                                    <InlineGrid gap="300" columns={['twoThirds', 'oneThird']}>
                                                        <Select
                                                            label="Applies to"
                                                            options={[{ label: model.purchaseType === 2 ? 'Specific subscription products' : 'Specific products', value: 2 }, { label: model.purchaseType === 2 ? 'All subscription products in a specific collection' : 'Specific collections', value: 1 }, { label: model.purchaseType === 2 ? 'All subscription products in an entire order' : 'Entire order', value: 0 }]}
                                                            value={model.getType}
                                                            onChange={(newValue) => handleChange('getType', newValue)}
                                                        />
                                                        {renderPurchaseTypeSelect(false)}
                                                    </InlineGrid>
                                                    {
                                                        model.getType !== 0 ?
                                                            renderResourcePicker('get', `Specific ${model.getType === 1 ? 'collections' : 'products'} must be added`, false, false)
                                                            : null
                                                    }
                                                    {renderPurchaseTypeHelpText()}
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion VOLUME */}
                                        {/* #region Tiers */}
                                        {
                                            model.discountSubType === 5 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 5 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="semibold">Tiers</Text>
                                                    <table className="tiers-table">
                                                        <thead>
                                                            <tr>
                                                                <th>No.</th>
                                                                <th>Title</th>
                                                                <th>{model.minRequirements.includes(2) ? 'Minimum quantity' : 'Minimum amount'}</th>
                                                                <th>{model.valueType === 1 ? 'Percentage off' : 'Fixed amount off'}</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        {
                                                            model.tiers !== null ?
                                                                <tbody>
                                                                    {
                                                                        model.tiers.map((item, index) => {
                                                                            return <tr key={index}>
                                                                                <td>{index + 1}.</td>
                                                                                <td>
                                                                                    <TextField
                                                                                        value={item.title}
                                                                                        placeholder={tierTitlePlaceholder(index, model.valueType)}
                                                                                        error={errorTier.indexOf(`${index}_title`) > -1}
                                                                                        onChange={(newValue) => handleChangeTier(`${index}_title`, newValue)}
                                                                                        onBlur={() => handleErrorTier(`${index}_title`, !item.title)}
                                                                                    />
                                                                                </td>
                                                                                <td className="tier-min">
                                                                                    {
                                                                                        model.minRequirements.includes(2) ?
                                                                                            <QuantityFieldCustom
                                                                                                fieldName={`${index}_minimumQuantity`}
                                                                                                value={item.minimumQuantity}
                                                                                                required={true}
                                                                                                error={errorTier.indexOf(`${index}_minimumQuantity`) > -1}
                                                                                                handleChange={handleChangeTier}
                                                                                                handleError={handleErrorTier}
                                                                                            />

                                                                                            :
                                                                                            <CurrencyFieldCustom
                                                                                                prefix={props.currencySymbol}
                                                                                                fieldName={`${index}_minimumAmount`}
                                                                                                value={item.minimumAmount}
                                                                                                required={true}
                                                                                                error={errorTier.indexOf(`${index}_minimumAmount`) > -1}
                                                                                                handleChange={handleChangeTier}
                                                                                                handleError={handleErrorTier}
                                                                                            />
                                                                                    }
                                                                                </td>
                                                                                <td className="tier-value">
                                                                                    {
                                                                                        model.valueType === 1 ?
                                                                                            <PercentageFieldCustom
                                                                                                fieldName={`${index}_valuePercentage`}
                                                                                                value={item.valuePercentage}
                                                                                                required={true}
                                                                                                error={errorTier.indexOf(`${index}_valuePercentage`) > -1}
                                                                                                handleChange={handleChangeTier}
                                                                                                handleError={handleErrorTier}
                                                                                            />
                                                                                            :
                                                                                            <CurrencyFieldCustom
                                                                                                prefix={props.currencySymbol}
                                                                                                fieldName={`${index}_valueFixedAmount`}
                                                                                                value={item.valueFixedAmount}
                                                                                                required={true}
                                                                                                error={errorTier.indexOf(`${index}_valueFixedAmount`) > -1}
                                                                                                handleChange={handleChangeTier}
                                                                                                handleError={handleErrorTier}
                                                                                            />
                                                                                    }
                                                                                </td>
                                                                                <td>
                                                                                    {
                                                                                        index > 0 ?
                                                                                            <Button variant="plain" tone="critical" onClick={() => handleRemoveTier(index)}><Icon source={DeleteIcon} /></Button>
                                                                                            : null
                                                                                    }
                                                                                </td>
                                                                            </tr>
                                                                        })
                                                                    }
                                                                </tbody>
                                                                : null
                                                        }
                                                    </table>
                                                    {
                                                        errorTier.length ?
                                                            <InlineError message="All fields in discount tiers are required" />
                                                            : null
                                                    }
                                                    <div className="new-tier">
                                                        <Button icon={PlusIcon} fullWidth={false} size="large" onClick={() => handleAddTier()}>Add new tier</Button>
                                                    </div>
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Tiers */}
                                        {/* #region Mix & match */}
                                        {
                                            model.discountSubType === 6 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 6 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="semibold">Sets</Text>
                                                    <table className="tiers-table mixes">
                                                        <thead>
                                                            <tr>
                                                                <th>No.</th>
                                                                <th>Applies to</th>
                                                                <th>Discount Value</th>
                                                                <th></th>
                                                            </tr>
                                                        </thead>
                                                        {
                                                            model.mixes !== null ?
                                                                <tbody>
                                                                    {
                                                                        model.mixes.map((item, index) => {
                                                                            return <tr key={index}>
                                                                                <td>{index + 1}.</td>
                                                                                <td>
                                                                                    <BlockStack gap="200">
                                                                                        <InlineGrid gap="200" columns={2}>
                                                                                            <Select
                                                                                                options={[{ label: 'Specific products', value: 2 }, { label: 'Specific collections', value: 1 }]}
                                                                                                value={item.getType}
                                                                                                onChange={(newValue) => handleChangeMix(`${index}_getType`, newValue)}
                                                                                            />
                                                                                            <Select
                                                                                                options={[{ label: 'Both', value: 0 }, { label: 'One-time purchase', value: 1 }, { label: 'Subscription', value: 2 }]}
                                                                                                value={item.purchaseType}
                                                                                                onChange={(newValue) => handleChangeMix(`${index}_purchaseType`, newValue)}
                                                                                            />
                                                                                        </InlineGrid>
                                                                                        {renderResourcePickerInline(item, index, 'get')}
                                                                                    </BlockStack>
                                                                                </td>
                                                                                <td className="mix-value">
                                                                                    <BlockStack gap="200">
                                                                                        <Tooltip active={item.valueType === 2 || item.valueType === 4} content={item.valueType === 2 ? "Fixed amount (per order)" : "Fixed amount (per item)"}>
                                                                                            <Select
                                                                                                options={[{ label: 'Percentage', value: 1 }, { label: 'Fixed amount (per order)', value: 2 }, { label: 'Fixed amount (per item)', value: 4 }, { label: 'Fixed price', value: 3 }]}
                                                                                                value={item.valueType}
                                                                                                onChange={(newValue) => handleChangeMix(`${index}_valueType`, newValue)}
                                                                                            />
                                                                                        </Tooltip>
                                                                                        {
                                                                                            item.valueType === 1 ?
                                                                                                <PercentageFieldCustom
                                                                                                    fieldName={`${index}_valuePercentage`}
                                                                                                    value={item.valuePercentage}
                                                                                                    required={true}
                                                                                                    error={errorMix.indexOf(`${index}_valuePercentage`) > -1}
                                                                                                    handleChange={handleChangeMix}
                                                                                                    handleError={handleErrorMix}
                                                                                                />
                                                                                                :
                                                                                                item.valueType === 2 || item.valueType === 4 ?
                                                                                                    <CurrencyFieldCustom
                                                                                                        prefix={props.currencySymbol}
                                                                                                        fieldName={`${index}_valueFixedAmount`}
                                                                                                        value={item.valueFixedAmount}
                                                                                                        required={true}
                                                                                                        error={errorMix.indexOf(`${index}_valueFixedAmount`) > -1}
                                                                                                        handleChange={handleChangeMix}
                                                                                                        handleError={handleErrorMix}
                                                                                                    />
                                                                                                    :
                                                                                                    <CurrencyFieldCustom
                                                                                                        prefix={props.currencySymbol}
                                                                                                        fieldName={`${index}_valueFixedPrice`}
                                                                                                        value={item.valueFixedPrice}
                                                                                                        required={true}
                                                                                                        error={errorMix.indexOf(`${index}_valueFixedPrice`) > -1}
                                                                                                        handleChange={handleChangeMix}
                                                                                                        handleError={handleErrorMix}
                                                                                                    />
                                                                                        }
                                                                                    </BlockStack>
                                                                                </td>
                                                                                <td>
                                                                                    {
                                                                                        index > 0 ?
                                                                                            <Button variant="plain" tone="critical" onClick={() => handleRemoveMix(index)}><Icon source={DeleteIcon} /></Button>
                                                                                            : null
                                                                                    }
                                                                                </td>
                                                                            </tr>
                                                                        })
                                                                    }
                                                                </tbody>
                                                                : null
                                                        }
                                                    </table>
                                                    <div className="new-tier">
                                                        <Button icon={PlusIcon} fullWidth={false} size="large" onClick={() => handleAddMix()}>Add new set</Button>
                                                    </div>
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Mix & match */}
                                        {/* #region Item exclusions */}
                                        {
                                            model.discountSubType === 3 || ((model.discountSubType === 1 || model.discountSubType === 5) && model.getType !== 2) ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 3 || ((model.discountSubType === 1 || model.discountSubType === 5) && model.getType !== 2) ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="medium">Item exclusions</Text>
                                                    <ChoiceList
                                                        choices={[
                                                            { label: 'No exclusions', value: 0 },
                                                            { label: 'Exclude specific products', value: 2 },
                                                            { label: 'Exclude specific collections', value: 1 },
                                                        ]}
                                                        selected={[model.excludeType]}
                                                        onChange={(newValue) => handleChange('excludeType', newValue[0])}
                                                    />
                                                    {
                                                        model.excludeType > 0 ?
                                                            renderResourcePicker('exclude', `Specific ${model.excludeType === 1 ? 'collections' : 'products'} must be added`, false, false)
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Item exclusions */}
                                        {/* #region Countries */}
                                        {
                                            model.discountSubType !== 0 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType !== 0 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="medium">Countries</Text>
                                                    <ChoiceList
                                                        choices={[
                                                            { label: 'All countries', value: 0 },
                                                            { label: 'Selected countries', value: 1 },
                                                            //{ label: 'Except those countries', value: 2 },
                                                        ]}
                                                        selected={[model.countryType]}
                                                        onChange={(newValue) => handleChange('countryType', newValue[0])}
                                                    />
                                                    {
                                                        model.countryType === 1 ?
                                                            <BlockStack gap="200">
                                                                <TextField
                                                                    value={''}
                                                                    placeholder={'Search countries'}
                                                                    prefix={<Icon source={SearchIcon} tone="subdued" />}
                                                                    error={error.countryType ? 'Specific countries must be added' : ''}
                                                                    connectedRight={<Button size="large" onClick={() => {
                                                                        setCountryPickerOptions({
                                                                            initialQuery: "",
                                                                            open: true
                                                                        });
                                                                        let cCodes = model.countries ? model.countries.split(',') : [];
                                                                        if (model.includeRestOfWorld) {
                                                                            cCodes.push('__');
                                                                        }
                                                                        setSelectedCountries(cCodes);
                                                                    }}>Browse</Button>}
                                                                    onChange={(newValue) => {
                                                                        setCountryPickerOptions({
                                                                            initialQuery: newValue,
                                                                            open: true
                                                                        });
                                                                        let cCodes = model.countries ? model.countries.split(',') : [];
                                                                        if (model.includeRestOfWorld) {
                                                                            cCodes.push('__');
                                                                        }
                                                                        setSelectedCountries(cCodes);
                                                                    }}
                                                                />
                                                                {
                                                                    countries().length ?
                                                                        <div className="selected-resources">
                                                                            <ResourceList
                                                                                items={countries()}
                                                                                renderItem={(item, id, index) => {
                                                                                    return (
                                                                                        <ResourceItem
                                                                                            verticalAlignment="center"
                                                                                            media={
                                                                                                item.code === '__' ?
                                                                                                    <div className="rest-of-world-icon"><Icon source={GlobeIcon} /></div>
                                                                                                    :
                                                                                                    <ReactCountryFlag countryCode={item.code} svg style={{ width: '1.5rem', height: '1.5rem' }} />
                                                                                            }
                                                                                        >
                                                                                            <InlineStack wrap={false}>
                                                                                                <div className="w-100">
                                                                                                    <Text>{item.name}</Text>
                                                                                                </div>
                                                                                                <div>
                                                                                                    <Button variant="monochromePlain" onClick={() => handleRemoveCountry(item.code)}><Icon source={XIcon} /></Button>
                                                                                                </div>
                                                                                            </InlineStack>
                                                                                        </ResourceItem>
                                                                                    );
                                                                                }}
                                                                            />
                                                                        </div>
                                                                        : null
                                                                }
                                                            </BlockStack>
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Countries */}
                                        {/* #region Exclude shipping rates */}
                                        {
                                            model.discountSubType === 4 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="medium">Shipping rates</Text>
                                                    <Checkbox
                                                        label="Exclude shipping rates over a certain amount"
                                                        checked={model.setMaximumShippingPrice}
                                                        onChange={(newValue) => handleChange('setMaximumShippingPrice', newValue)}
                                                    />
                                                    {
                                                        model.setMaximumShippingPrice ?
                                                            <div className="choice-list-input">
                                                                <CurrencyFieldCustom
                                                                    prefix={props.currencySymbol}
                                                                    fieldName="maximumShippingPrice"
                                                                    value={model.maximumShippingPrice}
                                                                    required={true}
                                                                    error={error.maximumShippingPrice ? "Shipping rate amount can't be blank" : ''}
                                                                    handleChange={handleChange}
                                                                    handleError={handleError}
                                                                />
                                                            </div>
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Exclude shipping rates */}
                                        {
                                            model.discountSubType === 4 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="medium">Shipping methods</Text>
                                                    <ChoiceList
                                                        choices={[
                                                            { label: 'All shipping methods', value: 0 },
                                                            { label: 'Selected shipping methods', value: 1, helpText: (model.shippingMethodType === 1 ? 'Choose from the list, or type to add your custom option.' : '') },
                                                        ]}
                                                        selected={[model.shippingMethodType]}
                                                        onChange={(newValue) => handleChange('shippingMethodType', newValue[0])}
                                                    />
                                                    {
                                                        model.shippingMethodType === 1 ?
                                                            <ShippingMethodPickerCustom
                                                                value={model.shippingMethods}
                                                                error={error.shippingMethodType ? 'Specific shipping methods must be added' : ''}
                                                                handleChange={handleChange}
                                                            />
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {
                                            model.discountSubType === 3 || model.discountSubType === 4 ?
                                                <Divider />
                                                : null
                                        }
                                        {/* #region Must buy items*/}
                                        {
                                            model.discountSubType === 3 || model.discountSubType === 4 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="medium">Purchase item requirements</Text>
                                                    <ChoiceList
                                                        choices={itemRequirementsChoices()}
                                                        selected={[model.itemRequirements]}
                                                        onChange={(newValue) => {
                                                            if (newValue[0] === 1 && model.buyType === 0) {
                                                                handleChange('buyType', 2);
                                                            }
                                                            else if (newValue[0] === 2 && model.getType === 0) {
                                                                handleChange('getType', 2);
                                                            }

                                                            handleChange('itemRequirements', newValue[0]);
                                                        }}
                                                    />
                                                    {
                                                        model.itemRequirements === 1 ?
                                                            renderResourcePicker('buy', `Specify ${model.buyType === 1 ? 'collections' : 'products'} that must be in the cart`, true, true)
                                                            : null
                                                    }
                                                    {
                                                        model.itemRequirements === 2 ?
                                                            renderResourcePicker('get', `Specify ${model.getType === 1 ? 'collections' : 'products'} that must not be in the cart`, true, true)
                                                            : null
                                                    }
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Must buy items*/}
                                        {/* #region Minimum purchase requirements */}
                                        {
                                            model.discountSubType === 1 || model.discountSubType === 3 || model.discountSubType === 4 || model.discountSubType === 6 ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 1 || model.discountSubType === 3 || model.discountSubType === 4 || model.discountSubType === 6 ?
                                                <BlockStack gap="200">
                                                    <Text fontWeight="medium">Minimum purchase requirements</Text>
                                                    <ChoiceList
                                                        allowMultiple
                                                        choices={[
                                                            { label: `Minimum purchase amount (${props.currencySymbol})`, value: 1, renderChildren: renderMinimumAmount },
                                                            { label: 'Minimum quantity of items', value: 2, renderChildren: renderMinimumQuantity },
                                                        ]}
                                                        selected={model.minRequirements}
                                                        onChange={(newValue) => handleChange('minRequirements', newValue)}
                                                    />
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Minimum purchase requirements */}
                                        {/* #region Discount Limits */}
                                        {
                                            model.discountSubType === 1 && (model.valueType !== 2 || model.appliesOnEachItem) ?
                                                <Divider />
                                                : null
                                        }
                                        {
                                            model.discountSubType === 1 && (model.valueType !== 2 || model.appliesOnEachItem) ?
                                                <BlockStack gap="200">
                                                    <BlockStack>
                                                        <Text fontWeight="medium">Discount Limits</Text>
                                                        <Text as="span" tone="subdued">{'Applies according to the "Discount allocation priority" setting.'}</Text>
                                                    </BlockStack>
                                                    <ChoiceList
                                                        allowMultiple
                                                        choices={maxLimitsChoices()}
                                                        selected={model.maxLimits}
                                                        onChange={(newValue) => handleChange('maxLimits', newValue)}
                                                    />
                                                </BlockStack>
                                                : null
                                        }
                                        {/* #endregion Discount Limits */}
                                    </FormLayout>
                                </Modal.Section>
                                :
                                <ResourceList
                                    items={discountSubTypes}
                                    renderItem={(item) => {
                                        return (
                                            <ResourceItem
                                                id={item.value}
                                                name={item.name}
                                                onClick={() => ref.current.open({
                                                    //id: 0,
                                                    index: -1,
                                                    discountType: item.value === 1 || item.value === 3 || item.value === 5 || item.value === 6 ? 1 : (item.value === 2 ? 2 : 3),
                                                    discountSubType: item.value,
                                                    title: '',
                                                    valueType: 1,
                                                    valueFixedAmount: null,
                                                    valueFixedPrice: null,
                                                    valuePercentage: item.value === 4 ? 100 : null,
                                                    getType: item.value !== 4 ? 2 : 0,
                                                    buyType: item.value === 2 ? 2 : 0,
                                                    excludeType: 0,
                                                    getCollectionResources: [],
                                                    getProductResources: [],
                                                    buyCollectionResources: [],
                                                    buyProductResources: [],
                                                    excludeCollectionResources: [],
                                                    excludeProductResources: [],
                                                    discountedValueType: 1,
                                                    minRequirements: item.value === 2 || item.value === 5 ? [2] : [],
                                                    minimumAmount: null,
                                                    minimumQuantity: null,
                                                    maxLimits: [],
                                                    maximumAmount: null,
                                                    maximumQuantity: null,
                                                    skipQuantity: null,
                                                    onQuantity: null,
                                                    setUsesPerOrderLimit: false,
                                                    usesPerOrderLimit: null,
                                                    appliesOnEachItem: false,
                                                    countryType: 0,
                                                    shippingMethodType: 0,
                                                    itemRequirements: 0,
                                                    setMaximumShippingPrice: false,
                                                    maximumShippingPrice: null,
                                                    purchaseType: 0,
                                                    autoAdd: false,
                                                    tiers: [{ title: '', minimumAmount: null, minimumQuantity: null, valueFixedAmount: null, valuePercentage: null }],
                                                    mixes: [{ getType: 2, purchaseType: 0, valueType: 1, valueFixedAmount: null, valuePercentage: null, valueFixedPrice: null, appliesOnEachItem: false }]
                                                })}
                                            >
                                                <div className="select-discount-type">
                                                    <div>
                                                        <Text variant="bodyMd" fontWeight="semibold" as="h3">
                                                            {item.name}
                                                        </Text>
                                                        <div>{item.description}</div>
                                                    </div>
                                                    <div>
                                                        <Icon source={ChevronRightIcon} tone="subdued" />
                                                    </div>
                                                </div>
                                            </ResourceItem>
                                        );
                                    }}
                                />
                        }
                    </div>
            }
        </Modal>
    );
});